import { Chat, useWebchat } from '@botpress/webchat'
import { Button, Card, Progress, Tabs, TabsContent, TabsList, TabsTrigger } from '@bpinternal/ui-kit-next'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ArrowRight, ExternalLink, FilePen, ListTodo, MessageSquare, MessagesSquare, Sparkles } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useRef, type FC } from 'react'
import LiteYouTubeEmbed from 'react-lite-youtube-embed'
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import { useMediaQuery } from 'usehooks-ts'
import AiBannerGradient from '~/assets/ai-banner-gradient.svg?react'
import { Boundary } from '~/components'
import { showPlanSelectionDialog } from '~/features/billing/components/PlanSelectionDialog'
import { useSetWorkspacePreferences, useWebchatEmbedScript } from '~/hooks'
import { trackEvent } from '~/providers'
import { useSuspenseQuery } from '~/services'
import { config } from '~/shared'
import { cn } from '~/utils'
import { getUseConfigStore } from '../../../webchat/stores/useConfigStore'
import { getUseBotOnboardingStore } from '../../stores/useBotOnboardingStore'
import { AnimatedFilesList } from './AnimatedFilesList'

type BaseProps = {
  botId: string
  workspaceId: string
}

export const WebchatOnboarding: FC<BaseProps> = ({ botId, workspaceId }) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const useBotOnboardingStore = getUseBotOnboardingStore({ workspaceId, botId })
  const deploymentProgress = useBotOnboardingStore((state) => state.deploymentProgress)
  const setDeploymentProgress = useBotOnboardingStore((state) => state.setDeploymentProgress)

  const { mutate: setWorkspacePreference } = useSetWorkspacePreferences(workspaceId)

  useEffect(() => {
    setWorkspacePreference({
      path: 'isOnboarding/$botId',
      params: { botId },
      value: false,
    })
  }, [])

  useEffect(() => {
    if (deploymentProgress.currentStep === 'completed') {
      setDeploymentProgress({
        ...deploymentProgress,
        currentStep: 'acknowledged',
      })
      return
    }
  }, [deploymentProgress, setDeploymentProgress])

  return (
    <div className="flex h-[100dvh]">
      <div className="mx-auto w-full max-w-[500px] p-4 lg:my-auto lg:w-fit lg:max-w-5xl lg:p-0">
        {isDesktop ? (
          // Desktop Layout
          <div className="grid grid-cols-[380px_500px] gap-20">
            <ChatComponent botId={botId} workspaceId={workspaceId} />
            <NextStepsComponent botId={botId} workspaceId={workspaceId} />
          </div>
        ) : (
          // Mobile/Tablet Layout
          <Tabs defaultValue="chat" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="chat" className="flex items-center space-x-2 text-sm">
                <MessageSquare className="h-4 w-4" />
                <span>Chat</span>
              </TabsTrigger>
              <TabsTrigger value="next-steps" className="flex items-center space-x-2 text-sm">
                <ListTodo className="h-4 w-4" />
                <span>Next Steps</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chat" className="mt-4 h-full w-full">
              <ChatComponent botId={botId} workspaceId={workspaceId} />
            </TabsContent>

            <TabsContent value="next-steps" className="mt-4 h-full w-full">
              <NextStepsComponent botId={botId} workspaceId={workspaceId} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  )
}

const ChatComponent: FC<BaseProps> = ({ botId, workspaceId }) => {
  const bot = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId }).data
  const webchatInstalledIntegration = Object.values(bot.integrations).find(
    (integration) => integration.name === 'webchat'
  )

  const hasFirstUserMessageBeenTracked = useRef(false)
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [])

  const botName = useConfigStore((state) => state.botName)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const botDescription = useConfigStore((state) => state.botDescription)
  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const soundEnabled = useConfigStore((state) => state.soundEnabled)
  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const footer = useConfigStore((state) => state.footer)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const email = useConfigStore((state) => state.email)
  const phone = useConfigStore((state) => state.phone)
  const termsOfService = useConfigStore((state) => state.termsOfService)
  const privacyPolicy = useConfigStore((state) => state.privacyPolicy)
  const website = useConfigStore((state) => state.website)
  const themeMode = useConfigStore((state) => state.themeMode)
  const radius = useConfigStore((state) => state.radius)
  const color = useConfigStore((state) => state.color)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const variant = useConfigStore((state) => state.variant)

  const clientId = webchatInstalledIntegration?.webhookId ?? ''
  const apiUrl = config.webchatUrl

  const {
    clientState,
    client,
    user: webchatUser,
    messages,
    isTyping,
    participants,
    on,
    newConversation,
  } = useWebchat({
    clientId,
    apiUrl,
    storageKey: `bp-webchat-${botId}`,
  })

  const { sendMessage, uploadFile, addMessageFeedback } = client ?? {}

  useEffect(() => {
    return on('conversation', () => {
      if (messages.length === 0) {
        sendMessage?.({
          text: 'Hey!',
          type: 'text',
        })
      }
    })
  }, [sendMessage, messages])

  useEffect(() => {
    // Track when user sends their first message (excluding the proactive "Hey!" message)
    // Only run this effect once when we have the first user message
    if (!hasFirstUserMessageBeenTracked.current && webchatUser?.userId) {
      const userMessages = messages.filter((message) => message.authorId === webchatUser.userId)

      // Hey + user message will be the first user message
      if (userMessages.length > 1) {
        hasFirstUserMessageBeenTracked.current = true
        trackEvent({
          type: 'onboarding_first_user_message',
          botId,
          workspaceId,
        })
      }
    }
  }, [messages, webchatUser?.userId, botId, workspaceId])

  return (
    <Boundary>
      <motion.div
        initial={{ opacity: 0, y: 35 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={cn('flex h-[600px] flex-col items-center gap-4 lg:h-[700px]')}
      >
        <Chat
          messages={messages}
          isTyping={isTyping}
          participants={participants}
          restartConversation={newConversation}
          user={webchatUser?.userId ? { id: webchatUser?.userId } : undefined}
          isLoading={clientState === 'connecting'}
          connected={clientState === 'connected' ? true : clientState === 'disconnected' ? false : undefined}
          sendMessage={sendMessage}
          uploadFile={uploadFile}
          addMessageFeedback={addMessageFeedback}
          allowFileUpload={allowFileUpload}
          className="h-full w-full"
          configuration={{
            botName,
            botAvatar,
            botDescription,
            feedbackEnabled,
            soundEnabled,
            composerPlaceholder,
            footer,
            storageLocation,
            email,
            phone,
            website,
            privacyPolicy,
            termsOfService,
            radius,
            color,
            fontFamily,
            variant,
            headerVariant,
            themeMode,
          }}
        />
      </motion.div>
    </Boundary>
  )
}

const NextStepsComponent = ({ botId, workspaceId }: BaseProps) => {
  const navigate = useNavigate()
  const { shareableLink } = useWebchatEmbedScript({ botId, workspaceId })

  const useBotOnboardingStore = getUseBotOnboardingStore({ workspaceId, botId })
  const isIndexing = useBotOnboardingStore((state) => state.isIndexing)
  const indexedFiles = useBotOnboardingStore((state) => state.indexedFiles)
  const totalFileCount = useBotOnboardingStore((state) => state.totalFileCount)

  return (
    <div className="flex flex-col gap-4">
      <span className="text-sm font-medium text-neutral-950">
        {isIndexing ? 'Your bot is learning from your content' : 'Your bot is ready!'}
      </span>
      <Progress value={isIndexing ? ((indexedFiles?.length ?? 0) / (totalFileCount ?? 1)) * 100 : 100} />
      <AnimatedFilesList workspaceId={workspaceId} botId={botId} />
      {!isIndexing && <NextStepCard />}
      <Card className="flex items-center overflow-hidden">
        <div className="h-full w-52">
          <AiBannerGradient className="h-full scale-x-125 scale-y-150" />
        </div>
        <div className="flex flex-col gap-3 p-8">
          <h2 className="text-xl font-semibold">Get access to more features</h2>
          <p className="text-sm">
            Custom branding, human handoff, and role-based access control are available on premium plans.
          </p>
          <Button
            variant={isIndexing ? 'default' : 'secondary'}
            className="mt-5 self-end"
            onClick={() => {
              trackEvent({ type: 'onboarding_upsell_clicked', botId, workspaceId })
              showPlanSelectionDialog({ workspaceId })
            }}
          >
            View Plans
          </Button>
        </div>
      </Card>
      <div className="mt-auto flex items-center justify-end gap-8">
        <a
          className="flex items-center gap-2 truncate text-sm font-medium text-neutral-950 decoration-neutral-800 hover:underline"
          href={shareableLink ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink className="size-4" />
          Share
        </a>
        <Button
          variant={isIndexing ? 'secondary' : 'default'}
          className="min-w-28"
          onClick={() =>
            navigate({
              to: '/workspaces/$workspaceId/bots/$botId',
              params: { workspaceId, botId },
            })
          }
        >
          {isIndexing ? 'Skip' : 'Continue'}
        </Button>
      </div>
    </div>
  )
}

const NextStepCard = () => {
  return (
    <Card className="flex flex-col gap-4 p-6 text-sm">
      <NextStepRow className="font-medium" icon={<ArrowRight size={16} />} text={'Next Steps'} />
      <div className="flex flex-col gap-3">
        <NextStepRow icon={<MessagesSquare size={16} />} text={'Test your bot'} />
        <NextStepRow icon={<FilePen size={16} />} text={'Enter Studio to fine tune your bot'} />
        <NextStepRow icon={<Sparkles size={16} />} text={'Extend your bot with integrations'} />
        <NextStepRow icon={<ExternalLink size={16} />} text={'Share your bot'} />
      </div>
    </Card>
  )
}

const NextStepRow = ({ className, text, icon }: { className?: string; text: string; icon: ReactNode }) => {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <p className={className}>{text}</p>
    </div>
  )
}

const OnboardingVideo = () => {
  useEffect(() => {
    const playButton = document.querySelector<HTMLButtonElement>('.lty-playbtn')
    if (playButton) {
      playButton.click()
    }
  }, [])
  return <LiteYouTubeEmbed title="Webchat Onboarding" id="8sbEwDA29yQ" />
}

OnboardingVideo.displayName = 'OnboardingVideo'
